// FILE: src/features/adminUsersSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetchWithAuth from "../utils/fetchWithAuth";

// Fetch all users
// GET /api/admin/users ?search &status &role &page &limit (≤ 100)
// → { users, totalCount, page, limit, totalPages } (WP-7). status: all |
// locked | in-progress | not-started | completed | invited | untimed |
// enrolment-ended; role: all | student | admin.
export const fetchUsers = createAsyncThunk(
  "adminUsers/fetchUsers",
  async ({ search, status, role, page, limit } = {}, { rejectWithValue }) => {
    try {
      const params = [];
      if (search) {
        params.push(`search=${encodeURIComponent(search)}`);
      }
      if (status && status !== "all") {
        params.push(`status=${encodeURIComponent(status)}`);
      }
      if (role && role !== "all") {
        params.push(`role=${encodeURIComponent(role)}`);
      }
      if (page) {
        params.push(`page=${page}`);
      }
      if (limit) {
        params.push(`limit=${limit}`);
      }
      let url = "/api/admin/users";
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }

      // Suppose the response is { data: [...], totalCount: number }
      const response = await fetchWithAuth(url);
      return response; // e.g. { data, totalCount }
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch users");
    }
  }
);
// Add a new user/student
export const addUser = createAsyncThunk(
  "adminUsers/addUser",
  async (newUser) => {
    return await fetchWithAuth("/api/admin/students", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newUser)
    });
  }
);

// Update an existing user
export const updateUser = createAsyncThunk(
  "adminUsers/updateUser",
  async ({ userId, updates }) => {
    return await fetchWithAuth(`/api/admin/users/${userId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates)
    });
  }
);


const adminUsersSlice = createSlice({
  name: "adminUsers",
  initialState: {
    list: [], // array of user objects
    totalCount: 0, // for pagination
    totalPages: 1,
    page: 1, // current page
    limit: 25, // items per page
    loading: false,
    error: null,
    lastInvited: null,
    requestId: null
  },
  reducers: {
    setPage(state, action) {
      state.page = action.payload;
    },
    setLimit(state, action) {
      state.limit = action.payload;
    }
  },
  extraReducers: (builder) => {
    // fetchUsers
    builder
      .addCase(fetchUsers.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        // Only the latest search counts (a slow earlier answer is dropped).
        if (state.requestId && action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.list = Array.isArray(action.payload?.users) ? action.payload.users : [];
        state.totalCount = Number(action.payload?.totalCount) || 0;
        state.totalPages = Number(action.payload?.totalPages) || 1;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        if (state.requestId && action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload || "Error fetching users";
      });

    // addUser / updateUser: the forms show their own failures (errorMessage),
    // so a refused save never puts an error on the student list.
    builder
      .addCase(addUser.fulfilled, (state, action) => {
        // POST /api/admin/students answers { message, user }; the list is
        // re-fetched when it is next shown, so it isn't patched here.
        state.lastInvited = action.payload?.user || null;
      })

      // updateUser: PATCH answers the safe view of the user.
      .addCase(updateUser.fulfilled, (state, action) => {
        const updatedUser = action.payload?.user || action.payload;
        if (!updatedUser?._id) return;
        const index = state.list.findIndex((u) => u._id === updatedUser._id);
        if (index !== -1) {
          state.list[index] = { ...state.list[index], ...updatedUser };
        }
      });
  }
});

export const { setPage, setLimit } = adminUsersSlice.actions;
export default adminUsersSlice.reducer;
